import { CUSTOM_FIELD_TYPE_KEY } from '@/constants/customFields'
import { GOAL_ACTIVITY_EVENTS } from '@/constants/goal'
import { initiativeMutation } from '@/constants/mutations'
import { DEFAULT_PREFERENCE } from '@/constants/team'
import dedup from '@/store/dedup'
import notification from '@/store/modules/notification'
import vision from '@/store/modules/vision'
import idea from '@/store/modules/idea'
import userLists from '@/store/modules/userLists'
import initiative from '@/store/modules/initiative'
import orgSelect from '@/store/modules/orgSelect'
import starred from '@/store/modules/starred'
import project from '@/store/modules/project'
import bulkAction from '@/store/modules/bulkAction'
import urlManager from '@/urlManager'
import makeOwnerships from '@/utils/makeOwnerships'
import { camelCaseToSnakeCase } from '@/utils/objectCasingConvertors'
import serializeParams from '@/utils/serializeParams'
import Vue from 'vue'
import Vuex from 'vuex'
import * as R from 'ramda'
import service from '../service'
import { ENTITY_TYPE } from '../constants/entities'
import announcement from '@/store/modules/announcement'
import { KR_ACTIVITY_EVENTS, KR_MEASURING_UNIT } from '@/constants/kr'
import feedPreference from '@/store/modules/feedPreference'

Vue.use(Vuex)

export const USER_STATUS = {
  UNKNOWN: 'unknown',
  LOGGED_IN: 'logged-in',
  NOT_LOGGED_IN: 'not-logged-in'
}

const store = new Vuex.Store({
  strict: process.env.VUE_APP_ENVIRONMENT !== 'production',
  modules: {
    announcement,
    notification,
    vision,
    orgSelect,
    idea,
    initiative,
    starred,
    feedPreference,
    userLists,
    project,
    bulkAction
  },
  state() {
    return {
      status: USER_STATUS.UNKNOWN,
      user: null,
      userCreated: false,
      users: {},
      orgs: {},
      teams: {},
      goals: {},
      krs: {},
      initiatives: {},
      visitedRoutes: [],
      orgCustomFields: {},
      customFieldDefinitions: {},
      userInteractions: {},
      enabledCustomFields: {
        teams: {},
        projects: {}
      },
      initiativeDisplayFields: { orgs: {}, teams: {}, projects: {} }
    }
  },
  getters: {
    status: state => state.status,
    user: state => state.user,
    userById: state => id => {
      return state.users[id] || null
    },
    orgById: state => id => {
      return state.orgs[id] || null
    },
    userOrgs: state => state.user?.organizations.slice().sort((a, b) => a.id - b.id),
    orgMemberships: state => id => {
      return state.orgs[id].memberships || []
    },
    teamById: state => id => {
      return state.teams[id] || null
    },
    teamMemberships: state => id => {
      let team = state.teams[id]
      if (team) {
        return team.memberships || []
      } else {
        return []
      }
    },
    userTeamsByOrgId: state => orgId => {
      return state.user.teams
        .filter(team => team.organizationId === +orgId)
        .map(team => state.teams[team.id])
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
    },
    userTeamsByOrgIdSubOrgId: state => (orgId, subOrgId) => {
      return state.user.teams
        .filter(team => team.organizationId === +orgId && team.parentId === subOrgId)
        .map(team => state.teams[team.id])
        .sort((a, b) => {
          return a.name.localeCompare(b.name)
        })
    },
    rootTeamByOrgId: state => orgId => {
      let teamList = state.user?.teams.filter(
        team => team.organizationId == orgId && !team.parentId
      )
      return teamList?.length > 0 ? teamList[0] : null
    },
    userTeamsExceptRootByOrgId: (state, getters) => orgId => {
      return getters.userTeamsByOrgId(orgId).filter(team => team.parentId)
    },
    computeMembershipTypeForTeamId: state => teamId => {
      const compute = teamId => {
        const membership = state.user.memberships.find(membership => membership.teamId === teamId)
        if (membership) {
          return membership.type
        }
        const team = state.teams[teamId]
        if (!team || !team.parentId) {
          return null
        }
        return compute(team.parentId)
      }
      return state.user && compute(teamId)
    },
    goalById: state => id => {
      return state.goals[id] || null
    },
    keyResultById: state => id => {
      for (const goalId of Object.keys(state.goals)) {
        const goal = state.goals[goalId]
        for (const kr of goal.keyResults) {
          if (Number(id) === kr.id) {
            return kr
          }
        }
      }
      return state.krs[id]
    },
    initiativeById: state => id => state.initiatives[id],
    customFieldDefinitions: state => orgId => state.orgCustomFields[orgId] || [],
    customFieldByOrgId: state => orgId =>
      (state.orgCustomFields[orgId] || []).filter(x => !x.projectId),
    customFieldDefinitionById: state => cfId => state.customFieldDefinitions[cfId],
    initiativeDisplayFieldsByTeamId: state => teamId => state.initiativeDisplayFields.teams[teamId],
    initiativeDisplayFieldsByProjectId: state => projectId =>
      state.initiativeDisplayFields.projects[projectId],
    initiativeEnabledCustomFieldsByTeamId: state => teamId =>
      state.enabledCustomFields.teams[teamId],
    initiativeEnabledCustomFieldsByProjectId: state => projectId =>
      state.enabledCustomFields.projects[projectId]
  },
  mutations: {
    SET_NOT_LOGGED_IN(state) {
      state.status = USER_STATUS.NOT_LOGGED_IN
    },
    SET_CURRENT_USER(state, { user, created = false }) {
      state.user = user
      if (user) {
        state.status = USER_STATUS.LOGGED_IN
        state.userCreated = created
        Vue.set(state.users, user.id, user)
      } else {
        state.status = USER_STATUS.NOT_LOGGED_IN
        state.userCreated = false
      }
    },
    SAVE_USER(state, user) {
      Vue.set(state.users, user.id, user)
    },
    SET_ORGS(state, orgs) {
      if (R.isNil(orgs) || R.isEmpty(orgs)) {
        state.orgs = {}
      } else {
        orgs.forEach(org => Vue.set(state.orgs, org.id, org))
      }
    },
    SAVE_ORG(state, org) {
      Vue.set(state.orgs, org.id, org)
    },
    SAVE_ORG_MEMBERSHIPS(state, { id, memberships }) {
      Vue.set(state.orgs[id], 'memberships', memberships)
    },
    SET_TEAMS(state, teams) {
      if (R.isNil(teams) || R.isEmpty(teams)) {
        state.teams = {}
      } else {
        teams.forEach(team => {
          team.preferences = team.preferences || DEFAULT_PREFERENCE
          Vue.set(state.teams, team.id, team)
        })
      }
    },
    DELETE_TEAM(state, teamId) {
      Vue.delete(state.teams, teamId)
    },
    SAVE_TEAM(state, team) {
      const memberships = state.teams[team.id]?.memberships
      team.memberships = memberships
      if (!team.preferences) {
        team.preferences = DEFAULT_PREFERENCE
      }
      Vue.set(state.teams, team.id, team)
    },
    SAVE_USER_TEAM(state, team) {
      state.user.teams.push(team)
    },
    UPDATE_USER_TEAM(state, team) {
      const index = state.user.teams.findIndex(x => x.id === team.id)
      Vue.set(state.user.teams, index, team)
    },
    SAVE_TEAM_MEMBERSHIPS(state, { id, memberships }) {
      Vue.set(state.teams[id], 'memberships', memberships)
    },
    UPDATE_ORG_MEMBER(state, { orgId, member }) {
      const index = state.orgs[orgId].memberships.findIndex(R.propEq('userId', member.userId))
      Vue.set(state.orgs[orgId].memberships, index, member)
    },
    ADD_TEAM_MEMBER(state, { teamId, member }) {
      const members = state.teams[teamId].memberships
      Vue.set(state.teams[teamId], 'memberships', [member, ...members])
    },
    UPDATE_TEAM_MEMBER(state, { teamId, member }) {
      const index = state.teams[teamId].memberships.findIndex(R.propEq('userId', member.userId))
      Vue.set(state.teams[teamId].memberships, index, member)
    },
    REMOVE_TEAM_MEMBER(state, { teamId, userId }) {
      const members = state.teams[teamId].memberships.filter(x => x.userId !== userId)
      Vue.set(state.teams[teamId], 'memberships', members)
    },
    SET_GOAL(state, goal) {
      const existingGoal = state.goals[goal.id]
      const latestGoal = { ...existingGoal, ...goal } // This is done to retain properties like parent_goals which is not part of all the responses
      Vue.set(state.goals, goal.id, latestGoal)
    },
    SET_KR(state, kr) {
      Vue.set(state.krs, kr.id, kr)
    },
    DELETE_GOAL(state, goalId) {
      Vue.delete(state.goals, goalId)
    },
    UPDATE_KR(state, kr) {
      const goal = state.goals[kr.goalId]
      if (goal) {
        const krIndex = goal.keyResults.findIndex(R.propEq('id', kr.id))
        state.goals = R.assocPath([kr.goalId, 'keyResults', krIndex], kr, state.goals)
      }
      Vue.set(state.krs, kr.id, kr)
    },
    DELETE_KR(state, { goalId, krId }) {
      let goal = state.goals[goalId]
      if (goal) {
        let krIndex = goal.keyResults.findIndex(kr => kr.id === krId)
        if (krIndex !== -1) {
          Vue.delete(goal.keyResults, krIndex)
        }
      }
    },
    RECORD_CHECKIN(state, checkin) {
      Object.values(state.goals).forEach(goal => {
        goal.keyResults.forEach(kr => {
          if (kr.id == checkin.keyResultId) {
            kr.status = checkin.newStatus
            kr.current = checkin.newCurrent
          }
        })
      })
    },
    SET_INITIATIVE(state, initiative) {
      Vue.set(state.initiatives, initiative.id, initiative)
    },
    DELETE_INITIATIVE(state, initiativeId) {
      Vue.delete(state.initiatives, initiativeId)
    },
    VISIT_ROUTE(state, route) {
      state.visitedRoutes = [...state.visitedRoutes, route]
    },
    REMOVE_POPPED_ROUTE(state) {
      state.visitedRoutes = R.remove(state.visitedRoutes.length - 2, 2, state.visitedRoutes)
    },
    SET_ORG_CUSTOM_FIELDS(state, { orgId, customFields }) {
      state.orgCustomFields = { ...state.orgCustomFields, [orgId]: customFields }
    },
    SET_CUSTOM_FIELDS(state, customFields) {
      customFields.forEach(cf => {
        state.customFieldDefinitions[cf.id] = cf
      })
    },
    SET_ENABLED_CUSTOM_FIELDS(state, { teamId, projectId, customFields }) {
      const enabledCustomFields = { ...state.enabledCustomFields }
      if (projectId) {
        enabledCustomFields.projects[projectId] = customFields
      } else {
        enabledCustomFields.teams[teamId] = customFields
      }
      state.enabledCustomFields = enabledCustomFields
    },
    SET_USER_INTERACTION(state, payload) {
      state.userInteractions = { ...state.userInteractions, ...payload }
    },
    [initiativeMutation.SET_DISPLAY_INITIATIVE_FIELDS](state, payload) {
      const displayFieldsConfig = { ...state.initiativeDisplayFields }
      if (payload.projectId) {
        displayFieldsConfig.projects[payload.projectId] = payload.fields
      } else if (payload.teamId) {
        displayFieldsConfig.teams[payload.teamId] = payload.fields
      } else {
        displayFieldsConfig.orgs[payload.orgId] = payload.fields
      }
      state.initiativeDisplayFields = displayFieldsConfig
    }
  },
  actions: {
    async invitationSignUp({ commit }, { email, password, invitationKey }) {
      const response = await service.client.post(urlManager.invitationSignUp, {
        email,
        password,
        invitationKey
      })
      const user = response.data.user
      commit('SET_CURRENT_USER', { user, created: true })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async login({ commit }, { email, password }) {
      const user = await service.login(email, password)
      commit('SET_CURRENT_USER', { user })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async logout({ commit, dispatch }) {
      try {
        await service.logout()
      } finally {
        commit('SET_CURRENT_USER', { user: null })
        commit('SET_ORGS', [])
        commit('SET_TEAMS', [])
        dispatch('orgSelect/clear')
      }
      return null
    },
    async getCurrentUserWithCreationFlag({ commit }, { created = false } = {}) {
      const user = await service.getCurrentUser()
      commit('SET_CURRENT_USER', { user, created: created })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async getCurrentUser({ dispatch }) {
      return await dispatch('getCurrentUserWithCreationFlag', { created: false })
    },
    async updateProfile({ commit }, userInfo) {
      let user = await service.updateProfile(userInfo)
      commit('SET_CURRENT_USER', { user })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async uploadProfilePic({ commit }, { blob }) {
      let user = await service.uploadProfilePic(blob)
      commit('SET_CURRENT_USER', { user })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async verifySignup({ commit }, { signUpId, email, code }) {
      const response = await service.client.post(urlManager.verifySignup(signUpId), {
        email,
        code
      })
      const user = response.data.user
      commit('SET_CURRENT_USER', { user, created: true })
      commit('SET_ORGS', user.organizations)
      commit('SET_TEAMS', user.teams)
      return user
    },
    async getUser({ commit, getters }, { id }) {
      let user = getters.userById(id)
      if (user) {
        return user
      } else {
        user = await dedup('getUser', { id }, async () => {
          return await service.getUser(id)
        })
        commit('SAVE_USER', user)
        return user
      }
    },
    async getOrg({ commit }, { id }) {
      const org = await service.getOrg(id)
      commit('SAVE_ORG', org)
      return org
    },
    async patchOrg({ commit }, { id, payload }) {
      const response = await service.client.patch(urlManager.orgDetails(id), payload)
      commit('SAVE_ORG', response.data)
      return response.data
    },
    async getOrgMemberships({ commit, getters }, { id }) {
      const rootTeam = getters.rootTeamByOrgId(id)
      if (!rootTeam) {
        throw new Error('root team not available')
      }
      const memberships = await service.getTeamMemberships(id, rootTeam.id)
      commit('SAVE_ORG_MEMBERSHIPS', { id, memberships })
      commit('SAVE_TEAM_MEMBERSHIPS', { id: rootTeam.id, memberships })
      memberships.forEach(membership => {
        commit('SAVE_USER', membership.user)
      })
      return memberships
    },
    async createTeam({ commit }, { orgId, name, isPrivate, isOrgLike, parentId, preferences }) {
      const response = await service.client.post(urlManager.teams(orgId), {
        name,
        isPrivate,
        isOrgLike,
        parentId,
        preferences
      })
      const team = response.data
      commit('SAVE_TEAM', team)
      commit('SAVE_USER_TEAM', team)
      return team
    },
    async editTeam(
      { commit, state },
      { orgId, teamId, name, isPrivate, parentId, isOrgLike, preferences }
    ) {
      const response = await service.client.put(urlManager.team({ orgId, teamId }), {
        name,
        isPrivate,
        parentId,
        isOrgLike,
        preferences
      })
      const team = response.data
      const existingTeamDetails = state.teams[teamId]
      // merge existing details since we don't want memberships to get removed
      commit('SAVE_TEAM', { ...existingTeamDetails, ...team })
      commit('UPDATE_USER_TEAM', team)
      return team
    },
    async patchTeam({ commit, state }, { orgId, teamId, payload }) {
      const response = await service.client.patch(urlManager.team({ orgId, teamId }), payload)
      const team = response.data
      const existingTeamDetails = state.teams[teamId]
      // merge existing details since we don't want memberships to get removed
      commit('SAVE_TEAM', { ...existingTeamDetails, ...team })
      return team
    },
    async archiveTeam({ dispatch }, { orgId, teamId, action }) {
      await service.client.post(urlManager.team({ orgId, teamId }), {
        action
      })
      await dispatch('getCurrentUser')
      await dispatch('getTeamMemberships', { orgId, teamId })
      return true
    },
    async deleteTeam({ commit, dispatch }, { orgId, teamId }) {
      await service.client.delete(urlManager.team({ orgId, teamId }))
      await dispatch('getCurrentUser')
      commit('DELETE_TEAM', teamId)
      return true
    },
    async getTeam({ commit }, { orgId, teamId, expand = [] }) {
      const response = await service.client.get(urlManager.team({ orgId, teamId }), {
        params: {
          expand: expand.join(',')
        }
      })
      const team = response.data
      commit('SAVE_TEAM', team)
      return team
    },
    async getTeamDescendants(options, { orgId, teamId, includeSelf = false, expand = null }) {
      const response = await service.client.get(urlManager.teamDescendants({ orgId, teamId }), {
        params: {
          expand: expand ? expand.join(',') : null,
          include_self: includeSelf
        }
      })
      return response.data
    },
    async getTeamMemberships({ commit, getters, dispatch }, { orgId, teamId }) {
      if (!getters.teamById(teamId)) {
        await dispatch('getTeam', { orgId, teamId })
      }
      const rootTeam = getters.rootTeamByOrgId(orgId)
      if (!rootTeam) {
        throw new Error('root team not available')
      }
      const memberships = await service.getTeamMemberships(orgId, teamId)
      commit('SAVE_TEAM_MEMBERSHIPS', { id: teamId, memberships })
      if (rootTeam.id == teamId) {
        console.log('saving org membershps')
        commit('SAVE_ORG_MEMBERSHIPS', { id: orgId, memberships })
      }
      memberships.forEach(membership => {
        commit('SAVE_USER', membership.user)
      })
    },
    async getGoals({ commit }, { orgId, teamId, options }) {
      const goals = await service.getGoals(orgId, teamId, options)
      goals.results.forEach(goal => {
        commit('SET_GOAL', goal)
      })
      return goals.results
    },
    async getGoalsAndMeta({ commit }, { orgId, teamId, options }) {
      const goals = await service.getGoals(orgId, teamId, options)
      goals.results.forEach(goal => {
        commit('SET_GOAL', goal)
      })
      return goals
    },
    async getGoal({ commit }, { orgId, goalId, options }) {
      const goal = await dedup('getGoal', { orgId, goalId }, async () => {
        const goal = await service.getGoal(orgId, goalId, options)
        commit('SET_GOAL', goal)
        return goal
      })
      return goal
    },
    async getCheckinHistory(options, { orgId, goalId }) {
      const response = await service.client.get(urlManager.checkInHistory({ orgId, goalId }))
      return response.data
    },
    async getKRs({ commit }, { orgId, orderBy, ...rest }) {
      const response = await service.client.get(urlManager.keyResults(orgId), {
        params: {
          order_by: orderBy || 'rank~id',
          ...camelCaseToSnakeCase(rest)
        }
      })
      response.data.forEach(kr => commit('SET_KR', kr))
      return response.data
    },
    async getKR({ commit }, { orgId, krId }) {
      const response = await service.client.get(urlManager.goals(orgId), {
        params: {
          key_result_id: krId
        }
      })
      const goals = response.data.results
      const goal = goals.length > 0 ? goals[0] : null
      if (!goal) {
        return null
      }
      const kr = goal.keyResults.find(akr => akr.id === krId)
      if (!kr) {
        return null
      }
      commit('SET_GOAL', goal)
      return kr
    },
    async getKRAlone({ commit }, { orgId, goalId, krId }) {
      return await dedup('getKRAlone', { orgId, goalId, krId }, async () => {
        const response = await service.client.get(urlManager.GoalKRDetail({ orgId, goalId, krId }))
        commit('SET_KR', response.data)
        return response.data
      })
    },
    downloadKRs(options, { orgId, orderBy, format, ...rest }) {
      let url = new URL('/api' + urlManager.keyResults(orgId), window.location.href)
      let params = new URLSearchParams(
        JSON.parse(
          JSON.stringify({
            order_by: orderBy || 'rank~id',
            format: format || 'xlsx',
            ...camelCaseToSnakeCase(rest)
          })
        )
      )
      url.host = window.location.host
      url.search = params.toString()
      window.location.href = url
    },
    async createNewGoal(
      { commit },
      {
        orgId,
        teamId,
        title,
        description,
        ownerId,
        coOwnerIds,
        timeline,
        parentId,
        checkinFrequency,
        keyResults
      }
    ) {
      let goal = await service.createNewGoal({
        orgId: orgId,
        teamId: teamId,
        title: title,
        description: description,
        ownerships: makeOwnerships(orgId, ownerId, coOwnerIds),
        timeline: timeline,
        parentId: parentId,
        checkinFrequency: checkinFrequency,
        keyResults: keyResults
          .map(kr => {
            if (kr.title) {
              const keyResult = {
                title: kr.title,
                measureBy: kr.measureBy,
                ownerships: makeOwnerships(orgId, kr.ownerId, kr.coOwnerIds)
              }
              kr.start !== '' && (keyResult.start = kr.start)
              kr.end !== '' && (keyResult.end = kr.end)
              return keyResult
            } else {
              return null
            }
          })
          .filter(kr => kr != null)
      })
      commit('SET_GOAL', goal)
      return goal
    },
    async updateGoal(
      { commit },
      {
        id,
        organizationId,
        type,
        teamId,
        title,
        description,
        ownerId,
        coOwnerIds,
        timeline,
        parentId,
        checkinFrequency,
        keyResults
      }
    ) {
      let updated = await service.updateGoal({
        id,
        orgId: organizationId,
        type,
        teamId,
        title,
        description,
        ownerships: makeOwnerships(organizationId, ownerId, coOwnerIds),
        timeline,
        parentId,
        checkinFrequency,
        keyResults: keyResults
          .map(kr => {
            if (kr.title) {
              const keyResult = {
                id: kr.id || null,
                title: kr.title,
                measureBy: kr.measureBy,
                current: kr.current || null,
                ownerships: makeOwnerships(organizationId, kr.ownerId, kr.coOwnerIds)
              }
              kr.start !== '' && (keyResult.start = kr.start)
              kr.end !== '' && (keyResult.end = kr.end)
              if (kr.start === '' && kr.end === '') {
                keyResult.start = 0
                keyResult.end = 100
                keyResult.measureBy = KR_MEASURING_UNIT.PERCENTAGE
              }
              return keyResult
            } else {
              return null
            }
          })
          .filter(kr => kr != null)
      })
      commit('SET_GOAL', updated)
      return updated
    },
    async patchGoal({ commit }, { id, organizationId, ...payload }) {
      let response = await service.client.patch(
        urlManager.goalDetail({ orgId: organizationId, goalId: id }),
        payload
      )
      commit('SET_GOAL', response.data)
      return response.data
    },
    async archiveGoal({ dispatch }, { id, organizationId, action, force }) {
      let response = await service.client.post(
        urlManager.goalDetail({ orgId: organizationId, goalId: id }),
        {
          action,
          force
        }
      )
      dispatch('getGoal', { orgId: organizationId, goalId: id })
      return response.data
    },
    async deleteGoal({ commit }, goal) {
      await service.deleteGoal(goal)
      setTimeout(() => {
        // remove goal from store after a while
        commit('DELETE_GOAL', goal.id)
      }, 100)
    },
    async archiveKr({ dispatch }, { id, organizationId, action, goalId, force }) {
      let response = await service.client.post(
        urlManager.GoalKRDetail({ orgId: organizationId, goalId, krId: id }),
        {
          action,
          force
        }
      )
      dispatch('getGoal', { orgId: organizationId, goalId })
      return response.data
    },
    async patchKR({ commit }, { organizationId, goalId, id, title, measureBy, start, end }) {
      const response = await service.client.patch(
        urlManager.GoalKRDetail({ orgId: organizationId, goalId, krId: id }),
        {
          title,
          measureBy,
          start,
          end
        }
      )
      commit('UPDATE_KR', response.data)
      return response.data
    },
    async deleteKR({ commit, dispatch }, { goalId, krId, orgId }) {
      await service.client.delete(urlManager.GoalKRDetail({ orgId, goalId, krId }))
      dispatch('getGoal', { orgId, goalId })
      setTimeout(() => {
        commit('DELETE_KR', { goalId, krId })
      }, 100)
    },
    async getGoalEvents(options, { orgId, goalId, isArchived }) {
      const response = await service.client.get(urlManager.goalEvents({ orgId, goalId }), {
        params: {
          is_archived: isArchived
        }
      })
      let events = response.data.filter(ev => {
        return [...KR_ACTIVITY_EVENTS, ...GOAL_ACTIVITY_EVENTS].some(type => type === ev.type)
      })
      return R.reverse(events)
    },
    async getKREvents(options, { orgId, krId }) {
      const response = await service.client.get(urlManager.krEvents({ orgId, krId }))
      let events = response.data.filter(ev => {
        return KR_ACTIVITY_EVENTS.some(type => type === ev.type)
      })
      return R.reverse(events)
    },
    async createEvent({ dispatch }, { orgId, goalId, event }) {
      let createdEvent = await service.createEvent(orgId, goalId, event)
      await dispatch('getGoal', { orgId, goalId })
      return createdEvent
    },
    async createKrEvent({ dispatch }, { orgId, krId, goalId, event }) {
      let createdEvent = await service.client.post(urlManager.krEvents({ orgId, krId }), event)
      await dispatch('getGoal', { orgId, goalId })
      return createdEvent
    },
    async updateGoalEvent(
      { dispatch },
      { orgId, goalId, eventId, comment, commentType, krId, checkin, notifySubscribers = false }
    ) {
      const url = krId
        ? urlManager.krEventDetails({ orgId, krId, eventId })
        : urlManager.goalEventDetails({ orgId, goalId, eventId })
      const response = await service.client.patch(
        url,
        { comment, commentType, checkin },
        {
          headers: {
            'X-Notify-Subscribers': notifySubscribers.toString()
          }
        }
      )
      await dispatch('getGoal', { orgId, goalId })
      return response.data
    },
    async deleteGoalEvent({ dispatch }, { orgId, goalId, eventId, krId }) {
      const url = krId
        ? urlManager.krEventDetails({ orgId, krId, eventId })
        : urlManager.goalEventDetails({ orgId, goalId, eventId })
      const response = await service.client.delete(url)
      await dispatch('getGoal', { orgId, goalId })
      return response
    },
    async getInitiatives(
      { commit },
      {
        orgId,
        orderBy,
        groupBy,
        krId,
        ownerIds,
        intervalStart,
        intervalEnd,
        intervalSubset,
        dueDate,
        impact,
        teamIds,
        projectId,
        sectionId,
        subOrgIds,
        isClosed,
        search,
        isArchived,
        includeCustomFields,
        includeStandardFields,
        scopeId,
        parentId,
        hasParent,
        limit,
        url,
        status,
        filterOp,
        nql
      }
    ) {
      let response
      if (url) {
        response = await service.client.get(url)
      } else {
        response = await service.client.get(urlManager.allInitiatives(orgId), {
          params: {
            order_by: orderBy || 'rank',
            key_result_id: krId,
            owner_ids: ownerIds,
            interval_start: intervalStart,
            interval_end: intervalEnd,
            interval_subset: intervalSubset,
            due_date: dueDate,
            impact: impact,
            team_ids: teamIds,
            project_id: projectId,
            section_id: sectionId,
            suborg_ids: subOrgIds,
            is_closed: isClosed,
            search,
            is_archived: isArchived,
            include_custom_fields: includeCustomFields,
            include_standard_fields: includeStandardFields,
            scope_id: scopeId,
            parent_id: parentId,
            limit: limit,
            has_parent: hasParent,
            status,
            filter_op: filterOp,
            group_by: groupBy,
            nql
          },
          paramsSerializer: serializeParams // custom serializer is needed because for array type query params(i.e filterOp), axios adds suffix - [] to the key of query param.
        })
      }
      response.data.results.forEach(initiative => {
        commit('SET_INITIATIVE', initiative)
      })
      return response.data
    },
    downloadInitiatives(
      options,
      {
        orgId,
        format,
        orderBy,
        groupBy,
        krId,
        ownerIds,
        intervalStart,
        intervalEnd,
        intervalSubset,
        dueDate,
        impact,
        teamIds,
        isClosed,
        search,
        isArchived,
        includeCustomFields,
        includeStandardFields,
        scopeId,
        parentId,
        hasParent,
        filterOp,
        nql,
        compareWithDate
      }
    ) {
      let url = new URL('/api' + urlManager.allInitiatives(orgId), window.location.href)
      let params = new URLSearchParams(
        JSON.parse(
          JSON.stringify({
            format: format || 'xlsx',
            order_by: orderBy || 'rank',
            group_by: groupBy,
            key_result_id: krId,
            owner_ids: ownerIds,
            interval_start: intervalStart,
            interval_end: intervalEnd,
            interval_subset: intervalSubset,
            due_date: dueDate,
            impact: impact,
            team_ids: teamIds,
            is_closed: isClosed,
            search,
            is_archived: isArchived,
            include_custom_fields: includeCustomFields,
            include_standard_fields: includeStandardFields,
            scope_id: scopeId,
            parent_id: parentId,
            has_parent: hasParent,
            nql: nql || undefined,
            compare_with_date: compareWithDate || undefined
          })
        )
      )
      url.host = window.location.host
      let paramsString = params.toString()
      if (filterOp) {
        filterOp.forEach(filter => {
          paramsString += `&filter_op=${filter}`
        })
      }
      url.search = paramsString
      window.location.href = url
    },
    async getInitiative(
      { commit },
      { orgId, initiativeId, includeCustomFields, includeStandardFields }
    ) {
      return await dedup('getInitiative', { orgId, initiativeId }, async () => {
        const response = await service.client.get(
          urlManager.initiativeDetail({ orgId, initiativeId }),
          {
            params: {
              include_custom_fields: includeCustomFields,
              include_standard_fields: includeStandardFields
            }
          }
        )
        commit('SET_INITIATIVE', response.data)
        return response.data
      })
    },
    async createInitiative(
      { commit },
      {
        orgId,
        goalId,
        krId,
        title,
        impact,
        ownerId,
        coOwnerIds = [],
        status,
        parentId,
        projectId,
        sectionId
      }
    ) {
      let response = await service.client.post(urlManager.allInitiatives(orgId), {
        goalId,
        keyResultId: krId,
        title,
        impact,
        ownerships: makeOwnerships(orgId, ownerId, coOwnerIds),
        status,
        parentId,
        projectId,
        sectionId
      })
      commit('SET_INITIATIVE', response.data)
      return response.data
    },
    async patchInitiative(
      { commit },
      {
        orgId,
        initiativeId,
        data,
        includeCustomFields = false,
        includeComputedFields,
        notifySubscribers = false,
        params = {}
      }
    ) {
      if (data.ownerId || (data.coOwnerIds && data.coOwnerIds.length > 0)) {
        data = {
          ...R.omit(['ownerId', 'coOwnerIds'], data),
          ownerships: makeOwnerships(orgId, data.ownerId, data.coOwnerIds)
        }
      }
      let response = await service.client.patch(
        urlManager.initiativeDetail({ orgId, initiativeId }),
        data,
        {
          params: {
            include_custom_fields: includeCustomFields,
            include_standard_fields: includeComputedFields,
            ...params
          },
          headers: {
            'X-Notify-Subscribers': notifySubscribers.toString()
          }
        }
      )
      commit('SET_INITIATIVE', response.data)
      return response.data
    },
    async deleteInitiatives({ commit }, { orgId, initiativeIds }) {
      let response = await service.client.post(urlManager.initiativeBulkAction(orgId), {
        initiativeIds,
        action: 'delete'
      })
      initiativeIds.forEach(id => commit('DELETE_INITIATIVE', id))
      return response.data
    },
    async moveInitiatives({ commit }, { orgId, payload, params }) {
      let response = await service.client.post(urlManager.initiativeBulkAction(orgId), payload, {
        params
      })
      const initiatives = response.data
      initiatives.forEach(initiative => {
        commit('SET_INITIATIVE', initiative)
      })
      return initiatives
    },
    async getInitiativeEvents(options, { orgId, id }) {
      let response = await dedup('getInitiativeEvents', { orgId, initiativeId: id }, async () => {
        return service.client.get(urlManager.initiativeEvents({ orgId, initiativeId: id }))
      })
      return response.data
    },
    async createInitiativeEvent(options, { orgId, id, comment, actorId, commentType }) {
      let response = await service.client.post(
        urlManager.initiativeEvents({ orgId, initiativeId: id }),
        {
          comment,
          commentType,
          initiativeId: id,
          actorId
        }
      )
      return response.data
    },
    async updateInitiativeEvent(
      options,
      { orgId, id, eventId, comment, commentType, notifySubscribers = false }
    ) {
      let response = await service.client.put(
        urlManager.initiativeEventDetails({ orgId, initiativeId: id, eventId }),
        {
          comment,
          commentType
        },
        {
          headers: { 'X-Notify-Subscribers': notifySubscribers.toString() }
        }
      )
      return response.data
    },
    async deleteInitiativeEvent(options, { orgId, id, eventId }) {
      let response = await service.client.delete(
        urlManager.initiativeEventDetails({ orgId, initiativeId: id, eventId })
      )
      return response.data
    },
    async getDisplayInitiativeFields({ commit }, { orgId, teamId, projectId }) {
      return dedup('getDisplayInitiativeFields', { orgId, teamId, projectId }, async () => {
        const response = await service.client.get(urlManager.displayInitiativeFields(orgId), {
          params: camelCaseToSnakeCase({ teamId, projectId })
        })
        commit(initiativeMutation.SET_DISPLAY_INITIATIVE_FIELDS, {
          orgId,
          teamId,
          projectId,
          fields: response.data
        })
        return response.data
      })
    },
    async attachCustomFields(_, { orgId, teamId, projectId, customFieldDefinitionIds }) {
      const response = await service.client.post(urlManager.enabledCustomFields(orgId), {
        customFieldDefinitionIds,
        teamId,
        projectId
      })
      return response.data
    },
    async detachCustomField(_, { orgId, enabledFieldId }) {
      const response = await service.client.delete(
        urlManager.enabledCustomFieldDetails({ orgId, enabledFieldId })
      )
      return response.data
    },
    async addDisplayInitiativeField(_, { orgId, teamId, projectId, payload }) {
      const response = await service.client.post(urlManager.displayInitiativeFields(orgId), {
        ...payload,
        ...camelCaseToSnakeCase({ teamId, projectId })
      })
      return response.data
    },
    async removeDisplayInitiativeField(_, { orgId, teamId, projectId, displayFieldId }) {
      const response = await service.client.delete(
        urlManager.displayInitiativeFieldDetails({ orgId, teamId, projectId, displayFieldId })
      )
      return response.data
    },
    async changeRank(options, { orgId, type, subjectId, beforeId, afterId }) {
      await service.client.post(urlManager.changeRank(orgId), {
        type,
        subjectId,
        beforeId,
        afterId
      })
    },
    async updateOrgMember({ commit, getters }, { orgId, userId, type }) {
      const rootTeam = getters.rootTeamByOrgId(orgId)
      if (!rootTeam) {
        throw new Error('root team not available')
      }
      const response = await service.client.put(
        urlManager.teamMembershipDetails({ orgId, teamId: rootTeam.id, userId }),
        {
          type,
          userId
        }
      )
      commit('UPDATE_ORG_MEMBER', { orgId, member: response.data })
      return response.data
    },
    async addMemberToTeam({ commit }, { orgId, teamId, userId, type }) {
      const response = await service.client.post(urlManager.teamMemberships({ orgId, teamId }), {
        userId,
        type
      })
      commit('ADD_TEAM_MEMBER', { member: response.data, teamId })
      return response.data
    },
    async updateTeamMember({ commit }, { orgId, userId, type, teamId }) {
      const response = await service.client.put(
        urlManager.teamMembershipDetails({ orgId, teamId, userId }),
        {
          type,
          userId
        }
      )
      commit('UPDATE_TEAM_MEMBER', { teamId, member: response.data })
      return response.data
    },
    async removeTeamMember({ commit }, { orgId, teamId, userId }) {
      const response = await service.client.delete(
        urlManager.teamMembershipDetails({ orgId, teamId, userId })
      )
      commit('REMOVE_TEAM_MEMBER', { teamId, userId })
      return response
    },
    async inviteUsers(options, { orgId, teamId, emails }) {
      const url = teamId
        ? urlManager.sendTeamInvite({ orgId, teamId })
        : urlManager.sendOrgInvite(orgId)
      const response = await service.client.post(url, {
        emails
      })
      return response.data
    },
    async changeOwnership({ commit }, { orgId, entityType, entity, ownerId, coOwnerIds }) {
      let url = null
      if (entityType === ENTITY_TYPE.GOAL) {
        url = urlManager.goalDetail({ orgId, goalId: entity.id })
      } else if (entityType === ENTITY_TYPE.KEY_RESULT) {
        url = urlManager.GoalKRDetail({ orgId, goalId: entity.goalId, krId: entity.id })
      } else if (entityType === ENTITY_TYPE.INITIATIVE) {
        url = urlManager.initiativeDetail({ orgId, initiativeId: entity.id })
      } else if (entityType === ENTITY_TYPE.IDEA) {
        url = urlManager.ideaDetails(orgId, entity.id)
      } else {
        throw new Error('Unknown entity', entityType)
      }
      let response = await service.client.patch(url, {
        ownerships: makeOwnerships(orgId, ownerId, coOwnerIds)
      })
      let updatedEntity = response.data
      if (entityType == ENTITY_TYPE.GOAL) {
        commit('SET_GOAL', updatedEntity)
      } else if (entityType == ENTITY_TYPE.KEY_RESULT) {
        commit('UPDATE_KR', updatedEntity)
      } else if (entityType == ENTITY_TYPE.INITIATIVE) {
        commit('SET_INITIATIVE', updatedEntity)
      }
      return updatedEntity
    },
    async requestGoalUpdate(options, { orgId, goalId }) {
      const response = await service.client.post(urlManager.goalDetail({ orgId, goalId }), {
        action: 'request-update'
      })
      return response.data
    },
    async requestKrUpdate(options, { orgId, goalId, krId }) {
      const response = await service.client.post(urlManager.GoalKRDetail({ orgId, goalId, krId }), {
        action: 'request-update'
      })
      return response.data
    },
    async getOrgCustomFields({ commit }, { orgId }) {
      const response = await dedup('getOrgCustomFields', { orgId }, async () => {
        return await service.client.get(urlManager.CFDefinitions(orgId))
      })
      commit('SET_ORG_CUSTOM_FIELDS', { orgId, customFields: response.data })
      commit('SET_CUSTOM_FIELDS', response.data)
      return response.data
    },
    async getEnabledCustomFields({ commit }, { orgId, teamId, projectId }) {
      const response = await dedup(
        'getEnabledCustomFields',
        { orgId, teamId, projectId },
        async () => {
          return await service.client.get(urlManager.enabledCustomFields(orgId), {
            params: camelCaseToSnakeCase({ teamId, projectId })
          })
        }
      )
      const customFields = response.data
      commit('SET_ENABLED_CUSTOM_FIELDS', { orgId, teamId, customFields, projectId })
      commit('SET_CUSTOM_FIELDS', customFields)
      return response.data
    },
    async getInitiativeCustomFields(options, { initiativeId, orgId }) {
      const response = await service.client.get(
        urlManager.initiativeCustomFields({ orgId, initiativeId })
      )
      return response.data
    },
    async setInitiativeCustomField(options, { orgId, initiativeId, field, value }) {
      const payload = { definitionId: field.id }
      const valueKey = CUSTOM_FIELD_TYPE_KEY[field.type]
      payload[valueKey] = value
      try {
        await service.client.put(
          urlManager.initiativeCustomFieldDetails({ orgId, initiativeId, fieldId: field.id }),
          payload
        )
      } catch (e) {
        if (e.response.status === 404) {
          await service.client.post(
            urlManager.initiativeCustomFields({ orgId, initiativeId }),
            payload
          )
        } else {
          throw e
        }
      }
    },
    async moveKR({ commit }, { orgId, krId, goalId, toGoalId }) {
      const url = urlManager.KRDetail({ orgId, krId, goalId })
      const response = await service.client.post(url, {
        action: 'move-kr',
        toGoalId
      })
      commit('SET_GOAL', response.data.fromGoal)
      commit('SET_GOAL', response.data.toGoal)
      return response.data
    },
    async getOauthClients(options, { orgId } = {}) {
      const response = await service.client.get(urlManager.oauthClients, {
        params: {
          check_is_installed: true,
          organization_id: orgId || null
        }
      })
      return response.data
    },
    async authorizeOAuthClient(options, { orgId, clientId }) {
      const response = await service.client.post(urlManager.oauthClientDetail(clientId), {
        action: 'authorize',
        organization_id: orgId
      })
      window.location = response.data.url
    },
    async removeOAuthAuthorization(options, { orgId, clientId }) {
      const response = await service.client.post(urlManager.oauthClientDetail(clientId), {
        action: 'remove_authorization',
        organization_id: orgId
      })
      return response.data
    }
  }
})

service.setErrorHandler(error => {
  if (
    error.response &&
    error.response.status === 403 &&
    error.response.data &&
    error.response.data.hasOwnProperty('code') &&
    error.response.data.code === 'not_authenticated'
  ) {
    if (store.getters.user) {
      store.commit('SET_CURRENT_USER', { user: null })
      store.commit('SET_ORGS', null)
      store.commit('SET_TEAMS', null)
    } else {
      store.commit('SET_NOT_LOGGED_IN')
    }
  }
})

export default store
